import { Component, computed, effect, ElementRef, inject, input, OnDestroy, signal, ViewChild } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { QaiAvatarComponent, QaiBadgeComponent, QaiIconKind, QaiSubtitlesComponent, QaiSubtitlesFormatting } from '@qai/ui';
import { SubtitleKind } from 'src/app/utils/enums/learner/subtitle-kind.enum';
import { TtsStreamService } from 'src/app/services/learner/tts-stream.service';
import { PathwayRoleplayFooterService } from 'src/app/pages/learner/call/call-pathway-roleplay/services/pathway-roleplay-footer.service';
import { injectAiNotice } from 'src/app/pages/learner/call/library/ai-notice/ai-notice';

@Component({
  imports: [TranslocoModule, QaiAvatarComponent, QaiBadgeComponent, QaiSubtitlesComponent],
  selector: 'app-pathway-call-footer',
  standalone: true,
  styleUrls: ['./call-footer.css'],
  templateUrl: './call-footer.html',
  providers: [TtsStreamService],
})
export class PathwayCallFooterComponent implements OnDestroy {
  // The account's AI notice in the sim's or learner's language; null hides it.
  protected readonly aiNotice = injectAiNotice();

  /** Caption text to show (e.g. from API). Falls back to FOOTER_SUBTITLE_READY when empty. */
  captionText = input<string>('');
  showAvatar = input<boolean>(true);
  /** Explicit subtitle text to drive qai-subtitles. When empty, falls back to captionText or default copy. */
  subtitleText = input<string>('');
  /** Visual state for subtitles (ready/speaking/listening/muted). */
  subtitleKind = input<SubtitleKind>(SubtitleKind.Ready);
  subtitleFormatting = input<QaiSubtitlesFormatting>('italic');
  subtitleIcon = input<QaiIconKind | undefined>(undefined);
  showAvatarPhoto = input<boolean>(false);
  avatarPhotoUrl = input<string>('');
  showSubtitles = input<boolean>(true);

  @ViewChild(QaiSubtitlesComponent, { read: ElementRef }) subtitlesRef?: ElementRef;

  private readonly ttsStreamService = inject(TtsStreamService);
  private readonly footerService = inject(PathwayRoleplayFooterService, {optional: true});
  private readonly ttsPlaying = signal(false);

  private readonly coachLine = computed(() => this.footerService?.coachLine() ?? null);

  protected readonly resolvedSubtitleText = computed(() =>
    this.coachLine() ? this.coachLine()!.text : this.subtitleText()
  );

  protected readonly resolvedSubtitleKind = computed(() =>
    this.coachLine()
      ? (this.ttsPlaying() ? SubtitleKind.Speaking : SubtitleKind.Ready)
      : this.subtitleKind()
  );

  protected readonly isAvatarSpeaking = computed(() => this.resolvedSubtitleKind() === SubtitleKind.Speaking);

  private scrollAnimation?: ReturnType<typeof setInterval>;
  private speakingEndSub?: ReturnType<typeof this.ttsStreamService.onSpeakingEnd$.subscribe>;

  constructor() {
    effect(() => {
      const line = this.coachLine();
      if (!line) return;
      this.ttsPlaying.set(true);
      this.ttsStreamService.playStream(line.audio$);
      this.speakingEndSub = this.ttsStreamService.onSpeakingEnd$.subscribe(() => {
        this.ttsPlaying.set(false);
      });
    });

    effect(() => {
      const text = this.resolvedSubtitleText();

      if (this.scrollAnimation) {
        clearInterval(this.scrollAnimation);
        this.scrollAnimation = undefined;
      }

      if (!text?.trim()) return;


      // Wait 150ms for DOM to render new text before measuring dimensions
      setTimeout(() => {
        if (!this.subtitlesRef?.nativeElement) return;
        const host = this.subtitlesRef.nativeElement as HTMLElement;
        const el = host.querySelector('.subtitle-text') as HTMLElement ?? host;
        const labelEl = host.querySelector('.state-label-container') as HTMLElement;

        // Compute available height precisely so host never overflows/clips subtitle-text
        const hostStyle = getComputedStyle(host);
        const hostPaddingTop = parseFloat(hostStyle.paddingTop);
        const labelHeight = labelEl ? labelEl.offsetHeight : 0;
        const elMarginTop = parseFloat(getComputedStyle(el).marginTop);
        const availableHeight = host.clientHeight - hostPaddingTop - labelHeight - elMarginTop;

        el.style.maxHeight = `${availableHeight}px`;
        el.style.overflowY = 'auto';
        el.style.scrollbarWidth = 'none';

        el.scrollTop = 0;

        // Estimate duration: ~160 words per minute average speech rate
        const wordCount = text.trim().split(/\s+/).length;
        const durationMs = Math.max((wordCount / 160) * 60 * 1000, 2000);
        const startTime = Date.now();
        const maxScroll = el.scrollHeight - el.clientHeight;

        if (maxScroll <= 0) return;

        this.scrollAnimation = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / durationMs, 1);
          el.scrollTop = progress * maxScroll;

          if (progress >= 1 && this.scrollAnimation) {
            clearInterval(this.scrollAnimation);
            this.scrollAnimation = undefined;
          }
        }, 50);
      }, 150);
    });
  }

  ngOnDestroy(): void {
    if (this.scrollAnimation) {
      clearInterval(this.scrollAnimation);
    }
    this.speakingEndSub?.unsubscribe();
  }
}
